import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isNil } from 'lodash';
import styled from 'styled-components';
import { MultiGrid } from 'react-virtualized';
import { setChartSettingsTableSettingsAction } from '../../../redux/slices/analyses';
import { useAppDispatch } from '../../../redux/hooks';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import {
  MultiGridTable,
  useTableColumnSortState,
  useTableColumnWidthState,
  useTableSearchState,
} from '../../MultiGridTable';
import * as ChartDataTableUI from './ChartDataTableUI';
import {
  calculateHeaderRowsOffset,
  filterDataTableRows,
  generateDataTableRows,
} from './utils';
import useChartDataTableCsvExport from './useChartDataTableCsvExport';
import { SecondaryButton } from '../../Button';
import { useChartDataTable } from './useChartDataTable';
import CellRenderer from './CellRenderer';
import { ChartMode } from '../types';
import SwitchiOS from '../../SwitchiOS';
import KubitTooltip from '../../KubitTooltip';
import UnSampleButton from '../UnSampleButton';
import { useChartContext } from '../ChartContext';
import { defaultTableSettings } from '../../../redux/utils/chartSettings';

interface IProps {
  analysisId: string;
  mode: ChartMode;
}

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`;

const ROW_HEIGHT = 32;
const TABLE_HEIGHT = 600;
const DEFAULT_CELL_WIDTH = 160;

export default ({ analysisId, mode }: IProps) => {
  const dispatch = useAppDispatch();
  const multiGridRef = useRef<MultiGrid>(null);
  const {
    chartData,
    metadata: { query },
    settings: { chartSettings },
    displayedMeasures,
  } = useChartContext();
  assertCondition(chartData?.queryType === QueryType.DataTable);
  const { tableSettings } = chartSettings;
  const isCompactMode = mode === ChartMode.Compact;

  const showUnSample =
    mode === ChartMode.Build &&
    !isNil(query?.samplePercentage) &&
    query.samplePercentage !== 100;

  const { executeCsvExport } = useChartDataTableCsvExport(analysisId);
  const { searchTerm, onSearch } = useTableSearchState(analysisId);
  const dataTableData = useChartDataTable();
  const {
    rows,
    breakdowns,
    hasBreakdown,
    hasSegment,
    hasComparison,
    segments,
    uniqueSegments,
  } = dataTableData;

  const columnSortStateData = useTableColumnSortState(analysisId);
  const { sortColumnIndex, sortDirection, resetSortState } =
    columnSortStateData;

  const filteredRows = filterDataTableRows({
    rows,
    searchTerm,
    sortColumnIndex,
    sortDirection,
  });

  const defaultColumnWidths = Array.from({ length: rows[0].length }).map(
    () => DEFAULT_CELL_WIDTH,
  );

  const columnWidthsData = useTableColumnWidthState({
    analysisId,
    initialColumnWidths: defaultColumnWidths,
  });

  const {
    isResizing,
    columnWidths,
    getColumnWidth,
    onChangeColumnWidths,
    multiGridWidth,
    setMultiGridWidth,
  } = columnWidthsData;

  const onResize = useCallback(() => {
    if (isResizing) {
      return;
    }
    if (multiGridRef?.current) {
      const multiGridWidth = multiGridRef.current.props.width;

      const allColumnsWidth = tableSettings?.columnWidths?.length
        ? tableSettings.columnWidths.reduce((a, b) => a + b, 0)
        : 0;

      // if the sum of all the column widths is less than the width of the multigrid
      // recompute the column widths
      if (
        multiGridWidth > 0 &&
        (isNil(tableSettings.columnWidths) || allColumnsWidth < multiGridWidth)
      ) {
        const newColWidths = Array.from({ length: rows[0].length }).map(
          () =>
            Math.max(DEFAULT_CELL_WIDTH, multiGridWidth / rows[0].length) - 1,
        );

        onChangeColumnWidths(newColWidths);
      }
    }
  }, [multiGridRef, rows, onChangeColumnWidths, tableSettings, isResizing]);

  // if the columnWidths array has changed, recompute the multigrid
  useEffect(() => {
    if (multiGridRef?.current) {
      multiGridRef.current.recomputeGridSize();

      // set the multigrid width state in the useTableColumnWidthState hook
      if (multiGridRef.current.props.width !== multiGridWidth) {
        setMultiGridWidth(multiGridRef.current.props.width);
      }
    }
  }, [multiGridRef, columnWidths, multiGridWidth, setMultiGridWidth]);

  // for the case of chart settings reset
  // if the columnWidths are nil, call onResize
  // to recompute the column widths
  useEffect(() => {
    if (isNil(tableSettings?.columnWidths)) {
      onResize();
    }
  }, [tableSettings, onResize]);

  const cellRenderer = useCallback(
    (gridCellProps) => {
      return (
        <CellRenderer
          analysisId={analysisId}
          dataTableData={dataTableData}
          columnWidthsData={columnWidthsData}
          columnSortStateData={columnSortStateData}
          filteredRows={filteredRows}
          tableSettings={tableSettings}
          displayedMeasures={displayedMeasures}
          {...gridCellProps}
        />
      );
    },
    [
      analysisId,
      dataTableData,
      columnWidthsData,
      columnSortStateData,
      filteredRows,
      tableSettings,
      displayedMeasures,
    ],
  );

  const numRows = useMemo(() => {
    const numBaseRows = filteredRows.length;

    // hasHeader=false because we don't want to calculate the header in this case
    // it's already set by the <Table /> component
    const offset = calculateHeaderRowsOffset(
      false,
      hasBreakdown,
      hasSegment,
      hasComparison,
    );

    return numBaseRows + offset;
  }, [filteredRows, hasBreakdown, hasSegment, hasComparison]);

  const tableHeight = useMemo(() => {
    const offset = calculateHeaderRowsOffset(
      true,
      hasBreakdown,
      hasSegment,
      hasComparison,
    );
    const numRows = filteredRows.length + offset;
    const tableHeight = numRows * ROW_HEIGHT + 10;

    if (tableHeight > TABLE_HEIGHT) {
      return TABLE_HEIGHT;
    }
    return tableHeight;
  }, [filteredRows, hasSegment, hasBreakdown, hasComparison]);

  const overscanIndicesGetter = useCallback(
    ({ direction, startIndex, stopIndex }) => {
      // custom logic to resolve the issue of dissappearing headers while scrolling
      // the headers span across numSegments, however react-virtualized recycles cells
      // on scroll, to keep the virtualized behavior.
      // this solution indicates that if we are scrolling horizontally, and the numSegments > 0
      // to prevent the unloading of the previous cells (which renders the header)
      if (direction === 'horizontal') {
        if (segments.length > 0) {
          return {
            overscanStartIndex: Math.max(startIndex - segments.length, 0),
            overscanStopIndex: stopIndex,
          };
        } else if (breakdowns.length > 0) {
          return {
            overscanStartIndex: Math.max(startIndex - breakdowns.length, 0),
            overscanStopIndex: stopIndex,
          };
        }
      }

      return {
        overscanStartIndex: startIndex,
        overscanStopIndex: stopIndex,
      };
    },
    [segments, breakdowns],
  );

  const showReset = searchTerm?.length > 0 || !isNil(sortColumnIndex);
  const onReset = () =>
    dispatch(
      setChartSettingsTableSettingsAction({
        analysisId,
        tableSettings: defaultTableSettings,
      }),
    );

  const toggleShowPercentDelta = () => {
    // reset the sort state
    // this is mainly because we store the column index of the sort
    // enabling the percent delta will change the number of columns
    resetSortState();

    const newShowPercentDelta = !tableSettings.showPercentDelta;
    const rows = generateDataTableRows(
      chartData,
      uniqueSegments,
      hasComparison,
      newShowPercentDelta,
    );
    const newColumnWidths = Array.from({ length: rows[0].length }).map(
      () => DEFAULT_CELL_WIDTH,
    );

    // update the column widths so that the new rows are rendered with the correct column count
    // prior to react-virtualized rendering the new rows
    onChangeColumnWidths(newColumnWidths);

    // update the table settings
    dispatch(
      setChartSettingsTableSettingsAction({
        analysisId,
        tableSettings: { showPercentDelta: newShowPercentDelta },
      }),
    );
  };

  return (
    <Container>
      {!isCompactMode && (
        <ChartDataTableUI.ContainerHeader>
          <ChartDataTableUI.StyledSearchInput
            value={searchTerm}
            onChange={onSearch}
          />
          <ChartDataTableUI.FlexRow>
            {hasComparison && (
              <ChartDataTableUI.PercentageToggle>
                <SwitchiOS
                  isChecked={tableSettings?.showPercentDelta ?? false}
                  onClick={toggleShowPercentDelta}
                />
                <div>Show % Change</div>
              </ChartDataTableUI.PercentageToggle>
            )}
            {showReset && (
              <KubitTooltip
                placement="bottom"
                content={'Reset to default view'}
              >
                <SecondaryButton
                  type="button"
                  onClick={onReset}
                  isOutlined
                  trackContext="reports"
                  trackAction="data table - reset"
                >
                  Reset
                </SecondaryButton>
              </KubitTooltip>
            )}
            {showUnSample && (
              <UnSampleButton
                analysisId={analysisId}
                label={`Report is sampled at a rate of ${query.samplePercentage}%`}
                disabled={query.schema.disabled}
              />
            )}
            <KubitTooltip
              placement="bottom"
              content={'Export the table to CSV'}
            >
              <SecondaryButton
                type="button"
                onClick={executeCsvExport}
                isOutlined
                trackContext="reports"
                trackAction="data table - export csv"
              >
                Export Table to CSV
              </SecondaryButton>
            </KubitTooltip>
          </ChartDataTableUI.FlexRow>
        </ChartDataTableUI.ContainerHeader>
      )}
      <ChartDataTableUI.Container $tableHeight={tableHeight}>
        {rows.length && (
          <MultiGridTable
            ref={multiGridRef}
            columnCount={rows[0].length}
            rowCount={numRows}
            cellRenderer={cellRenderer}
            rowHeight={ROW_HEIGHT}
            columnWidthRenderer={getColumnWidth}
            onResize={onResize}
            overscanIndicesGetter={overscanIndicesGetter}
            fixedRowCount={calculateHeaderRowsOffset(
              true,
              hasBreakdown,
              hasSegment,
            )}
          />
        )}
      </ChartDataTableUI.Container>
    </Container>
  );
};
